import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../sass/components/PasswordInput.module.scss';
import Button from './Button';
import TextInput from './TextInput';

export interface PasswordInputProps {
  autocomplete?: string;
  className?: string;
  errorMessage?: string;
  hint?: string;
  onChange?: (event: FormEvent<HTMLInputElement>) => void;
  placeholder?: string;
  title?: string;
  value?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  /** The id of the element that labels the box (see TextInput). */
  labelledBy?: string;
  required?: boolean;
  onBlur?: () => void;
}

let passwordIdCounter = 0;

export function PasswordInput({
  autocomplete,
  className,
  errorMessage,
  hint,
  onChange,
  placeholder,
  title,
  value,
  disabled,
  id,
  name,
  labelledBy,
  required,
  onBlur,
}: PasswordInputProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  // The toggle names the box it acts on (aria-controls), so the box needs an
  // id even when the caller has none.
  const [autoId] = useState(() => {
    passwordIdCounter += 1;
    return `password-input-${passwordIdCounter}`;
  });
  const inputId = id || autoId;

  // A toggle button (QA 2026-09-25 #31): one name, "Show password", and its
  // state in aria-pressed, rather than a label that flips between "show"
  // and "hide" with no state at all.
  const passwordShow = (
    <Button
      className={styles.passwordShow}
      onClick={() => setShow(!show)}
      aria-label={t('auth.showPassword')}
      aria-pressed={show}
      aria-controls={inputId}
      inactive={disabled}
    >
      {t('auth.show')}
    </Button>
  );

  return (
    <TextInput
      id={inputId}
      name={name}
      autoComplete={autocomplete}
      className={className}
      errorMessage={errorMessage}
      hint={hint}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      rightButton={passwordShow}
      title={title}
      type={show ? 'text' : 'password'}
      value={value}
      disabled={disabled}
      labelledBy={labelledBy}
      required={required}
      data-mixpanel-mask
    />
  );
}
